import { useCallback, useState } from "react";

import { CoachExecutionMode, CoachConversationScope } from "./types";

import InputComponent from "V2.0/components/InputComponent";
import { Button } from "V2.0/common/button";
import { message } from "antd";
import { showErrorToast } from "V2.0/utils";

export default AssistantCoachExecution;

function getInitialCoachExecutionState(coachExecutionSettings) {
  const { executionMode, timeoutMs } = coachExecutionSettings;

  return {
    executionMode: executionMode ?? CoachExecutionMode.Synchronous,
    timeoutMs: timeoutMs ?? 8000,
  };
}

const DEFAULT_RECENT_MESSAGE_PAIRS = 5;

function getInitialCoachContextState(coachContextSettings) {
  const { conversationScope, recentMessagePairs } = coachContextSettings;

  return {
    conversationScope: conversationScope ?? CoachConversationScope.Recent,
    recentMessagePairs: recentMessagePairs ?? DEFAULT_RECENT_MESSAGE_PAIRS,
  };
}

function AssistantCoachExecution(props) {
  const { assistantCoach, isUnlocked, onUpdateAssistant } = props;

  const [coachExecution, setCoachExecution] = useState(() =>
    getInitialCoachExecutionState(assistantCoach?.executionSettings ?? {}),
  );
  const [coachContext, setCoachContext] = useState(() =>
    getInitialCoachContextState(assistantCoach?.contextSettings ?? {}),
  );

  const [loading, setLoading] = useState<boolean>(false);

  const handleSave = useCallback(function (executionSettings, contextSettings) {
    const payload = {
      executionSettings,
      contextSettings,
    };

    const lifecycleCbs = {
      init() {
        setLoading(true);
        message.loading("Updating Execution Settings");
      },
      success() {
        message.destroy();
        setLoading(false);
        message.success("Execution Settings Updated");
      },
      error(e) {
        message.destroy();
        setLoading(false);
        showErrorToast(e, "Failed to update execution settings");
      },
    };

    onUpdateAssistant(payload, lifecycleCbs);
  }, []);

  return (
    <div className="w-2/4 min-w-[500px] p-4">
      <div className="border rounded-lg">
        <div className="bg-gray-50 rounded-t-lg px-6 py-3">
          <p className="text-md font-semibold">Execution Settings</p>
        </div>
        <div className="p-8 space-y-8">
          <InputComponent
            type="select"
            name="executionMode"
            label="Execution Mode"
            value={coachExecution.executionMode}
            options={[
              {
                label: "Synchronous",
                value: CoachExecutionMode.Synchronous,
              },
              {
                label: "Asynchronous",
                value: CoachExecutionMode.Asynchronous,
              },
            ]}
            onChange={(e) => {
              setCoachExecution((prev) => {
                return {
                  ...prev,
                  executionMode: e.target.value,
                };
              });
            }}
            disabled={!isUnlocked}
          />

          <InputComponent
            type="number"
            label="Timeout (milliseconds)"
            value={coachExecution.timeoutMs}
            disabled={!isUnlocked}
            onChange={(e) => {
              setCoachExecution((prev) => {
                return {
                  ...prev,
                  timeoutMs: Number(e.target.value),
                };
              });
            }}
          />

          <InputComponent
            type="select"
            name="conversationScope"
            label="Conversation Visible to Coach"
            value={coachContext.conversationScope}
            options={[
              {
                label: "Recent message pairs only",
                value: CoachConversationScope.Recent,
              },
              {
                label: "Full conversation",
                value: CoachConversationScope.Full,
              },
            ]}
            onChange={(e) => {
              setCoachContext((prev) => {
                return {
                  ...prev,
                  conversationScope: e.target.value,
                };
              });
            }}
            disabled={!isUnlocked}
          />

          {coachContext.conversationScope === CoachConversationScope.Recent && (
            <InputComponent
              type="number"
              label="Recent Message Pairs"
              value={coachContext.recentMessagePairs}
              min={1}
              disabled={!isUnlocked}
              onChange={(e) => {
                setCoachContext((prev) => {
                  return {
                    ...prev,
                    recentMessagePairs: Math.max(
                      1,
                      Number(e.target.value) || DEFAULT_RECENT_MESSAGE_PAIRS,
                    ),
                  };
                });
              }}
            />
          )}
        </div>
        {isUnlocked && (
          <div className="bg-gray-50 rounded-b-lg text-right px-6 py-3">
            <Button
              loading={loading}
              disabled={loading}
              onClick={() => handleSave(coachExecution, coachContext)}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
